import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClientError } from 'graphql-request';
import { type MyMemberInfo, memberInfoKey } from '@/hooks/member/memberInfoApi';
import type { CreditCardAccount } from '@/lib/payments/creditCardAccount.types';
import { parseExpiry } from '@/lib/payments/paymentAccount.format';
import type { AddCreditCardInput } from '@/lib/payments/paymentAccount.types';
import { mapToAutoPaySummary, requestEnableMyAutoPay } from '../autoPay/autoPayApi';
import {
  promoteDefault,
  type SavedPaymentMethodsData,
  savedPaymentMethodsKey,
  toBillingAddressInput,
} from '../paymentApi';
import { PaymentAddCardError, PaymentVerifyError, requestCreateCardProfile, requestVerifyCard } from './creditCardApi';

interface UseAddCreditCardOptions {
  onSuccess?: () => void;
}

/**
 * Submits a new credit card via the two-step gateway flow: verify (raw PAN/CVV → token) then
 * create the profile (token + last4 only). Raw PAN/CVV never reach the create step or telemetry.
 */
export function useAddCreditCard({ onSuccess }: UseAddCreditCardOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: AddCreditCardInput) => {
      const pan = input.cardNumber.replace(/\D/g, '');
      const { month: expirationMonth, year: expirationYear } = parseExpiry(input.expiry);

      const verified = await requestVerifyCard({ cardNumber: pan, cvv: input.cvv, expirationMonth, expirationYear });
      const last4 = pan.slice(-4);

      const { id } = await requestCreateCardProfile({
        gatewayTokenId: verified.gatewayTokenId,
        authTransactionId: verified.authTransactionId,
        last4,
        network: verified.network,
        // No client-side signal distinguishes commercial vs. consumer cards — verify only returns
        // network. Matches legacy (mdvipconnect forms.ts): always CONSUMER, no detection logic.
        cardType: 'CONSUMER',
        nameOnCard: input.billing.name,
        expiryMonth: expirationMonth,
        expiryYear: expirationYear,
        hsa: input.isHsa,
        isDefault: input.makeDefault,
        billingAddress: toBillingAddressInput(input.billing),
        // input.autoPay is never sent here: addCreditCard has no field to persist per-card AutoPay
        // enrollment. Member.autoPay is a member-level flag written by the separate
        // enableMyAutoPay mutation below, not part of the card record.
      });

      // Skip the call entirely when we already know (from the same cache that locks the checkbox)
      // that the member is enrolled — avoids a redundant round trip and Salesforce read. The
      // backend's own idempotent pre-check remains the fallback if this cache is stale.
      const cachedMemberInfo = queryClient.getQueryData<MyMemberInfo>(memberInfoKey);
      const alreadyEnrolled = cachedMemberInfo ? mapToAutoPaySummary(cachedMemberInfo).autoPayEnrolled : false;
      if (input.autoPay && !alreadyEnrolled) {
        // Enrollment failure must not block the card save — swallow and track only.
        try {
          await requestEnableMyAutoPay();
        } catch (err) {
          if (err instanceof Error) {
            trackException(err, { source: 'enableMyAutoPay', context: 'addCreditCard' });
          }
        }
      }

      // Echo of what we just submitted — every field here is data we already sent, not a guess.
      // Lets the list show the new card immediately instead of waiting on the invalidate-triggered
      // refetch (a live Salesforce round trip that can take a few seconds).
      const newCard: CreditCardAccount = {
        id,
        network: verified.network,
        last4,
        expirationMonth,
        expirationYear,
        holderName: input.billing.name,
        line1: input.billing.line1,
        line2: input.billing.line2,
        city: input.billing.city,
        state: input.billing.state,
        zip: input.billing.zip,
        isDefault: input.makeDefault,
        isExpired: false,
        isHsa: input.isHsa,
        // A freshly created profile is always active in Salesforce.
        isActive: true,
      };
      return newCard;
    },
    onSuccess: (newCard) => {
      queryClient.setQueryData<SavedPaymentMethodsData>(savedPaymentMethodsKey, (existing) => {
        const withNewCard: SavedPaymentMethodsData = {
          cards: [...(existing?.cards ?? []), newCard],
          bankAccounts: existing?.bankAccounts ?? [],
        };
        // The new default (if any) atomically demotes the prior one server-side, across both cards
        // and bank accounts — mirror that here so the optimistic list doesn't show a stale badge
        // anywhere until the refetch reconciles it.
        return newCard.isDefault ? promoteDefault(withNewCard, newCard.id) : withNewCard;
      });
      void queryClient.invalidateQueries({ queryKey: memberInfoKey });
      void queryClient.invalidateQueries({ queryKey: savedPaymentMethodsKey });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      // PaymentVerifyError wraps domain Failure variants (DECLINED, UNSUPPORTED_NETWORK);
      // wire value is kept for telemetry, prefixed to PAYMENT_VERIFY_* for i18n key lookup.
      // PaymentAddCardError wraps addCreditCard's Failure variant — no real codes exist yet
      // (RESERVED placeholder), so it falls through to the generic UNKNOWN message.
      const isDomainError = error instanceof PaymentVerifyError || error instanceof PaymentAddCardError;
      const code = isDomainError ? error.code : extractGqlErrorCode(error);
      const errorKey = error instanceof PaymentVerifyError ? `PAYMENT_VERIFY_${code}` : code;
      if (error instanceof Error) {
        // ClientError.message serializes request variables (CardNumber/CVV on the verify step).
        // Track a sanitized error to prevent PCI data reaching Application Insights.
        const trackable = error instanceof ClientError ? new Error(`addCreditCard: ${code}`) : error;
        trackException(trackable, { source: 'addCreditCard', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(errorKey), variant: 'destructive' });
    },
  });

  return { submit: (input: AddCreditCardInput) => mutation.mutate(input), isPending: mutation.isPending };
}
